// Step 5 — fitter preference.
//
// "Any Fitter" is the default. The user can also pick a specific fitter at
// the selected location. The list comes from /resources filtered to the
// selected location.
//
// Wireframe reference: mobile screenshot 5:01:22.

import { useEffect, useState } from 'react';
import styles from '../styles/widget.module.css';
import { listResources } from '../api/client';
import type { Resource } from '../types';
import type { BookingAction, BookingState } from '../state/machine';
import Info from '../assets/icons/Info';
import SimplePopup from '../components/SimplePopup';
import { useOpenClose } from '../hooks/useOpenClose';

interface FitterSelectProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  disabled?: boolean;
}

export function FitterSelect({ state, dispatch, disabled = false }: FitterSelectProps) {
  const [fitters, setFitters] = useState<Resource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { isOpen: isOpenFitterInfo, close: closeFitterInfo, open: openFitterInfo } = useOpenClose()

  useEffect(() => {
    if (!state.selectedLocation) return;
    listResources()
      .then((all) =>
        setFitters(all.filter((r) => r.locationId === state.selectedLocation!.id)),
      )
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load fitters'));
  }, [state.selectedLocation]);

  return (
    <div className={`${styles.selectFitterWrapper} ${disabled ? styles.panelSectionDisabled : ''}`}>

      <h3 className={styles.label}>
        Fitter{' '}
        <button type='button' onClick={openFitterInfo} className={styles.infoPopupTrigger}>
          <Info />
        </button>
      </h3>

      {disabled && <span className={styles.disabledFieldMessage}>Select a fitting type to enable this field</span>}
      <SimplePopup
        children={
          <div>
            <h3>WHAT MAKES A MASTER FITTER</h3>
            <p>Our master fitters graduate from Club Champion University with over 250 hours of classroom and in bay fitting experience.To stay ahead of the curve, they undergo rigorous, ongoing education directly from golf's top manufacturers every single year. This continuous training builds unmatched, cross-brand product knowledge, ensuring they know exactly how every head and shaft combination performs for your unique swing.
              If you have an existing fitter relationship, choose that fitter here. If not, a master fitter will be assigned to you.</p>
          </div>
        }
        isOpen={isOpenFitterInfo}
        onClosePopup={closeFitterInfo}
        className={styles.fitterInfoPopup}
      />

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.fitterList}>
        <div
          className={`${styles.fitterRow} ${state.selectedFitterId === null && state.selectedTime === null ? '' : ''} ${state.selectedFitterId === null ? styles.selected : ''
            }`}
          onClick={() => {
            if (disabled) return;
            dispatch({ type: 'SELECT_FITTER', payload: null });
          }}
          role="button"
          tabIndex={0}
        >
          Any Fitter
        </div>

        {fitters?.map((f) => (
          <div
            key={f.id}
            className={`${styles.fitterRow} ${state.selectedFitterId === f.id ? styles.selected : ''}`}
            onClick={() => {
              if (disabled) return;
              dispatch({ type: 'SELECT_FITTER', payload: f.id });
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                dispatch({ type: 'SELECT_FITTER', payload: f.id });
              }
            }}
          >
            {f.name}
          </div>
        ))}

        {!fitters && <p className={styles.subtitle}><span className={styles.spinner} />Loading fitters…</p>}
      </div>
    </div>
  );
}
